/**
 * Utility functions for date and time formatting
 */

/**
 * Таймзона, в которой админ-панель показывает все отметки, графики и события.
 *
 * Время учёта не должно зависеть от настроек компьютера пользователя:
 * браузер с устаревшей tzdata считает Asia/Almaty как UTC+6 (Казахстан перешёл
 * на единый UTC+5 с 01.03.2024) и сдвигает все отметки на час вперёд, а HR
 * из другого часового пояса увидел бы чужие смены смещёнными.
 *
 * Переопределяется через VITE_APP_TIME_ZONE на этапе сборки.
 */
export const APP_TIME_ZONE: string = import.meta.env.VITE_APP_TIME_ZONE || 'Asia/Almaty'

/**
 * Format a date string to localized date and time
 * Handles ISO 8601 strings, timestamps, and various date formats
 */
export function formatDateTime(dateString: string | number | Date) {
  try {
    // Handle null/undefined
    if (!dateString) {
      return { date: 'Не указано', time: '' }
    }

    // Parse the date - handle ISO strings, timestamps, Date objects
    let date: Date

    if (typeof dateString === 'string') {
      // ISO 8601 format with timezone (e.g., "2024-03-15T10:30:00.000Z" or "2024-03-15T10:30:00+06:00")
      date = new Date(dateString)
    } else if (typeof dateString === 'number') {
      // Unix timestamp
      date = new Date(dateString)
    } else {
      // Already a Date object
      date = dateString
    }

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date:', dateString)
      return { date: 'Неверная дата', time: '' }
    }

    return {
      date: date.toLocaleDateString('ru-RU', {
        timeZone: APP_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
      time: date.toLocaleTimeString('ru-RU', {
        timeZone: APP_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    }
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return { date: 'Ошибка формата', time: '' }
  }
}

/**
 * Format date only (without time)
 */
export function formatDate(dateString: string | number | Date) {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверная дата'
    }

    return date.toLocaleDateString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Format time only (without date)
 */
export function formatTime(dateString: string | number | Date, includeSeconds: boolean = false) {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверное время'
    }

    const options: Intl.DateTimeFormatOptions = {
      timeZone: APP_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
    }

    if (includeSeconds) {
      options.second = '2-digit'
    }

    return date.toLocaleTimeString('ru-RU', options)
  } catch (error) {
    console.error('Error formatting time:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Календарная дата (YYYY-MM-DD) в таймзоне приложения.
 *
 * Замена для toISOString().split('T')[0] и toLocaleDateString('en-CA') без
 * таймзоны: оба дают не тот день на границе суток.
 */
export function toAppDateString(dateString: string | number | Date = new Date()): string {
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('en-CA', { timeZone: APP_TIME_ZONE })
}

/**
 * Сегодняшняя дата (YYYY-MM-DD) в таймзоне приложения, со сдвигом на N дней.
 */
export function todayAppDateString(offsetDays = 0): string {
  return toAppDateString(Date.now() + offsetDays * 24 * 60 * 60 * 1000)
}

/**
 * Первое число месяца (YYYY-MM-DD), в который попадает дата, в таймзоне приложения.
 */
export function startOfMonthAppDateString(
  dateString: string | number | Date = new Date()
): string {
  return `${toAppDateString(dateString).slice(0, 8)}01`
}

/**
 * Format relative time (e.g., "2 часа назад")
 */
export function formatRelativeTime(dateString: string | number | Date): string {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверная дата'
    }

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSec = Math.floor(diffMs / 1000)
    const diffMin = Math.floor(diffSec / 60)
    const diffHour = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHour / 24)

    if (diffSec < 60) return 'только что'
    if (diffMin < 60) return `${diffMin} мин. назад`
    if (diffHour < 24) return `${diffHour} ч. назад`
    if (diffDay < 7) return `${diffDay} дн. назад`

    return formatDate(date)
  } catch (error) {
    console.error('Error formatting relative time:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Format date range
 */
export function formatDateRange(
  startDate: string | number | Date,
  endDate: string | number | Date
): string {
  const start = formatDate(startDate)
  const end = formatDate(endDate)
  return `${start} — ${end}`
}

/**
 * Parse date string to ISO format for API
 */
export function toISOString(dateString: string | number | Date): string {
  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }
    return date.toISOString()
  } catch (error) {
    console.error('Error converting to ISO string:', dateString, error)
    throw error
  }
}
